/**
 * ArchiveErrorPanel - Error Display with Platform-Specific Guidance
 *
 * Shows helpful error messages and recovery actions based on:
 * - Platform (Electron vs Web)
 * - Error type (connection, auth, config)
 *
 * @module @humanizer/studio/components/archive/ArchiveErrorPanel
 */

import React from 'react';
import { useArchive, type ArchivePlatform } from '../../contexts/ArchiveContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ArchiveErrorPanelProps {
  onRetry?: () => void;
  onSetup?: () => void;
  onLogin?: () => void;
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// ERROR CLASSIFICATION
// ═══════════════════════════════════════════════════════════════════════════

type ErrorCategory = 'connection' | 'auth' | 'config' | 'service' | 'unknown';

function categorizeError(error: string | null, platform: ArchivePlatform): ErrorCategory {
  if (!error) return 'unknown';

  const lowerError = error.toLowerCase();

  // Authentication errors
  if (
    lowerError.includes('401') ||
    lowerError.includes('unauthorized') ||
    lowerError.includes('authentication')
  ) {
    return 'auth';
  }

  // Configuration errors
  if (
    lowerError.includes('not configured') ||
    lowerError.includes('configuration') ||
    lowerError.includes('setup')
  ) {
    return 'config';
  }

  // Service unavailable
  if (
    lowerError.includes('503') ||
    lowerError.includes('unavailable') ||
    lowerError.includes('health check')
  ) {
    return 'service';
  }

  // Connection errors
  if (
    lowerError.includes('connection') ||
    lowerError.includes('econnrefused') ||
    lowerError.includes('timeout') ||
    lowerError.includes('network')
  ) {
    return 'connection';
  }

  return 'unknown';
}

// ═══════════════════════════════════════════════════════════════════════════
// ERROR CONTENT BY CATEGORY
// ═══════════════════════════════════════════════════════════════════════════

interface ErrorContent {
  icon: string;
  title: string;
  message: string;
  suggestions: string[];
  primaryAction?: {
    label: string;
    handler: 'retry' | 'setup' | 'login';
  };
  secondaryAction?: {
    label: string;
    handler: 'retry' | 'setup' | 'login';
  };
}

function getElectronErrorContent(category: ErrorCategory, error: string | null): ErrorContent {
  switch (category) {
    case 'connection':
      return {
        icon: '🔌',
        title: 'Database Connection Failed',
        message: error || 'Unable to connect to PostgreSQL.',
        suggestions: [
          'Check if PostgreSQL is running',
          'Verify the connection settings (host, port, database)',
          'Ensure the database exists and is accessible',
        ],
        primaryAction: { label: 'Retry Connection', handler: 'retry' },
        secondaryAction: { label: 'Update Settings', handler: 'setup' },
      };

    case 'config':
      return {
        icon: '⚙️',
        title: 'Configuration Needed',
        message: 'The archive database needs to be configured.',
        suggestions: [
          'Set up your PostgreSQL connection',
          'Install PostgreSQL if not already installed',
          'Optionally configure Ollama for AI features',
        ],
        primaryAction: { label: 'Configure Database', handler: 'setup' },
      };

    case 'service':
      return {
        icon: '🛠️',
        title: 'Service Unavailable',
        message: error || 'The database service is not responding.',
        suggestions: [
          'PostgreSQL may need to be restarted',
          'Check if the database server is overloaded',
          'Review PostgreSQL logs for errors',
        ],
        primaryAction: { label: 'Retry', handler: 'retry' },
      };

    default:
      return {
        icon: '❌',
        title: 'Archive Error',
        message: error || 'An unexpected error occurred.',
        suggestions: [
          'Try reconnecting to the database',
          'Check your configuration settings',
          'Review the application logs for details',
        ],
        primaryAction: { label: 'Retry', handler: 'retry' },
        secondaryAction: { label: 'Reconfigure', handler: 'setup' },
      };
  }
}

function getWebErrorContent(category: ErrorCategory, error: string | null): ErrorContent {
  switch (category) {
    case 'auth':
      return {
        icon: '🔐',
        title: 'Sign In Required',
        message: 'Please sign in to access your archive.',
        suggestions: [
          'Sign in with your Google account',
          'Your archive is stored securely in the cloud',
        ],
        primaryAction: { label: 'Sign In', handler: 'login' },
      };

    case 'service':
      return {
        icon: '☁️',
        title: 'Service Temporarily Unavailable',
        message: error || 'The archive service is currently unavailable.',
        suggestions: [
          'This is usually temporary',
          'Try again in a few moments',
          'Check status.humanizer.com for updates',
        ],
        primaryAction: { label: 'Retry', handler: 'retry' },
      };

    case 'connection':
      return {
        icon: '📡',
        title: 'Connection Problem',
        message: error || 'Unable to reach the archive service.',
        suggestions: [
          'Check your internet connection',
          'The service may be temporarily unavailable',
          'Try refreshing the page',
        ],
        primaryAction: { label: 'Retry', handler: 'retry' },
      };

    default:
      return {
        icon: '❌',
        title: 'Archive Error',
        message: error || 'An unexpected error occurred.',
        suggestions: [
          'Try refreshing the page',
          'Sign out and sign back in',
          'Contact support if the problem persists',
        ],
        primaryAction: { label: 'Retry', handler: 'retry' },
      };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ArchiveErrorPanel({
  onRetry,
  onSetup,
  onLogin,
  className = '',
}: ArchiveErrorPanelProps) {
  const { platform, error, refresh } = useArchive();

  const category = categorizeError(error, platform);
  const content =
    platform === 'electron'
      ? getElectronErrorContent(category, error)
      : getWebErrorContent(category, error);

  const handleAction = (handler: 'retry' | 'setup' | 'login') => {
    switch (handler) {
      case 'retry':
        if (onRetry) {
          onRetry();
        } else {
          refresh();
        }
        break;
      case 'setup':
        onSetup?.();
        break;
      case 'login':
        onLogin?.();
        break;
    }
  };

  return (
    <div className={`archive-error-panel ${className}`}>
      <div className="archive-error-panel__content">
        <div className="archive-error-panel__icon">{content.icon}</div>
        <h3 className="archive-error-panel__title">{content.title}</h3>
        <p className="archive-error-panel__message">{content.message}</p>

        {content.suggestions.length > 0 && (
          <ul className="archive-error-panel__suggestions">
            {content.suggestions.map((suggestion, i) => (
              <li key={i} className="archive-error-panel__suggestion">
                {suggestion}
              </li>
            ))}
          </ul>
        )}

        <div className="archive-error-panel__actions">
          {content.primaryAction && (
            <button
              className="archive-error-panel__button archive-error-panel__button--primary"
              onClick={() => handleAction(content.primaryAction!.handler)}
            >
              {content.primaryAction.label}
            </button>
          )}
          {content.secondaryAction && (
            <button
              className="archive-error-panel__button archive-error-panel__button--secondary"
              onClick={() => handleAction(content.secondaryAction!.handler)}
            >
              {content.secondaryAction.label}
            </button>
          )}
        </div>
      </div>

      {/* Platform-specific help links */}
      {platform === 'electron' && (
        <div className="archive-error-panel__help">
          <p>Need help?</p>
          <ul>
            <li>
              <a href="https://postgresapp.com" target="_blank" rel="noopener noreferrer">
                Install Postgres.app (macOS)
              </a>
            </li>
            <li>
              <a href="https://ollama.com" target="_blank" rel="noopener noreferrer">
                Install Ollama
              </a>
            </li>
            <li>
              <a href="https://humanizer.com/docs/local-setup" target="_blank" rel="noopener noreferrer">
                Local Setup Guide
              </a>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default ArchiveErrorPanel;
